import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from "../../../lib/supabaseClient";

// Import icons
import retailIcon from './SVG/1_CHARIOT.png';
import shoppingIcon from './SVG/2_SHOPPING.png';
import hotelIcon from './SVG/3_SONETTE.png';
import officeIcon from './SVG/8_OFFICE_STROKE.png';
import restaurantIcon from './SVG/5_CUISINE.png';
import culturalIcon from './SVG/6_PEINTURE.png';
import residenceIcon from './SVG/7_SERRURE.png';
import urbanIcon from './SVG/8_BANC.png';
import clusterIcon from './SVG/logo16.png';
import city from './SVG/city3.png';
import city_card from './SVG/city_horizontal.png';
import * as api from '../../../api';
import monthlyarchiIcon from './MonthlyIcons/architecture.png';
import monthlyculturalIcon from './MonthlyIcons/culture.png';
import monthlyhotelIcon from './MonthlyIcons/hotel.png';
import monthlyretailIcon from './MonthlyIcons/retail.png';
import monthlyresidenceIcon from './MonthlyIcons/private.png';
import monthlyofficeIcon from './MonthlyIcons/office.png';
import monthlyurbanIcon from './MonthlyIcons/urban.png';
import monthlyrestaurantIcon from './MonthlyIcons/restaurant.png';

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

const POINT_ICONS = {
    "Retail": shoppingIcon,
    "Hotel": hotelIcon,
    "Office": officeIcon,
    "Restaurant/bar": restaurantIcon,
    "Cultural space": culturalIcon,
    "Private residence": residenceIcon,
    "Urban space/Transportation": urbanIcon
};

const MONTHLY_ICONS = {
    "Retail": monthlyretailIcon,
    "Hotel": monthlyhotelIcon,
    "Office": monthlyofficeIcon,
    "Restaurant/bar": monthlyrestaurantIcon,
    "Cultural space": monthlyculturalIcon,
    "Private residence": monthlyresidenceIcon,
    "Urban space/Transportation": monthlyurbanIcon,
    "Architecture": monthlyarchiIcon
};

const WorldmapAdminView = () => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const sourceLoaded = useRef(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);

    // Get item data from URL params using window.location
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const itemId = params.get('itemId');
        const itemDataString = params.get('item'); // Keep for backward compatibility
        
        console.log('URL params:', window.location.search);
        console.log('Item ID:', itemId);
        
        // Try sessionStorage first (new method)
        if (itemId) {
            try {
                const storedData = sessionStorage.getItem(itemId);
                if (storedData) {
                    const itemData = JSON.parse(storedData);
                    console.log('Parsed item data from sessionStorage:', itemData.titre_affiche || itemData.nom_etablissement);
                    setSelectedItem(itemData);
                    return;
                }
            } catch (e) {
                console.error('Failed to parse item data from sessionStorage:', e);
            }
        }
        
        // Fallback to URL parameter method (backward compatibility)
        if (itemDataString) {
            console.log('Falling back to URL parameter method');
            try {
                // First try the new base64 decoding method
                const decodedString = decodeURIComponent(escape(atob(itemDataString)));
                const itemData = JSON.parse(decodedString);
                console.log('Parsed item data successfully:', itemData.titre_affiche || itemData.nom_etablissement);
                setSelectedItem(itemData);
            } catch (e) {
                console.error('Failed to parse item data with base64, trying fallback:', e);
                try {
                    // Fallback to old method for backward compatibility
                    const itemData = JSON.parse(decodeURIComponent(itemDataString));
                    console.log('Parsed item data with fallback:', itemData.titre_affiche || itemData.nom_etablissement);
                    setSelectedItem(itemData);
                } catch (fallbackError) {
                    console.error('Both decoding methods failed:', fallbackError);
                }
            }
        }
    }, []);

    // Load a single icon
    const loadIcon = async (mapInstance, iconKey, iconPath) => {
        return new Promise((resolve, reject) => {
            if (!mapInstance.hasImage(iconKey)) {
                const image = new Image();
                image.src = iconPath;
                image.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = image.width;
                    canvas.height = image.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(image, 0, 0);
                    mapInstance.addImage(iconKey, ctx.getImageData(0, 0, canvas.width, canvas.height));
                    resolve();
                };
                image.onerror = reject;
            } else {
                resolve();
            }
        });
    };

    // Load all icons
    const loadAllIcons = async (mapInstance) => {
        try {
            // Load regular icons
            await Promise.all(
                Object.entries(POINT_ICONS).map(([key, path]) => 
                    loadIcon(mapInstance, key, path)
                )
            );

            // Load monthly icons
            await Promise.all(
                Object.entries(MONTHLY_ICONS).map(([key, path]) => 
                    loadIcon(mapInstance, `monthly-${key}`, path)
                )
            );

            // Load special icons
            await Promise.all([
                loadIcon(mapInstance, 'cluster-icon', clusterIcon),
                loadIcon(mapInstance, 'city-icon', city),
                loadIcon(mapInstance, 'points-of-sale-icon', retailIcon)
            ]);

        } catch (error) {
            console.error("Error loading icons:", error);
        }
    };

    const setupPopupsAndHandlers = () => {
        if (!map.current) return;

        const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 15,
            maxWidth: '300px'
        });

        // Add hover effects for points
        map.current.on('mouseenter', 'vectorLayer', (e) => {
            map.current.getCanvas().style.cursor = 'pointer';
            const coordinates = e.features[0].geometry.coordinates.slice();
            const properties = e.features[0].properties;

            // Create popup content based on project type
            const popupContent = properties.projectType === 'itc' 
                ? `
                    <div class="mappopup" style="width: 200px; padding:0; border:1px solid; margin:none;">
                        <img src="${city_card}" alt="In The City" style="width: 80%; height: auto;display:block; margin-bottom: 8px;margin:auto">
                        <div class="infocontainer">
                            <div style="margin-bottom: 4px; color:blue; font-weight: bold;">
                                ${properties.nom_etablissement}
                            </div>
                            <div style="font-size: 12px; text-transform:none; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;">
                                ${properties.description_projet || ''}
                            </div>
                        </div>
                    </div>
                `
                : properties.projectType === 'pos' 
                ? `
                    <div class="mappopup" style="width: 200px;padding:0; border:1px solid;margin:none;">
                        <img style="max-width:80%; margin:auto;display:block" src="${properties.photos_projet ? properties.photos_projet.split(',')[0].trim() : retailIcon}" alt="Project">
                        <div class="infocontainer">
                            <div style="font-weight: bold; margin-bottom: 4px;color: red">Point of sale</div>
                            <div style="margin-bottom: 4px;color:blue;font-weight: bold;">${properties.design_points_of_sales}</div>
                            <div style="font-weight: bold;">${properties.titre_affiche}</div>
                        </div>
                    </div>
                `
                : properties.isSelected
                ? `
                    <div class="mappopup" style="width: 200px;padding:0; border:2px solid #667eea;margin:none;">
                        <div style="font-weight: bold;text-align:center;background-color:#667eea;color:white;padding:5px">Item Sélectionné</div>
                        <img src="${properties.photos_projet?.split(',')[0].trim()}" alt="Project" style="width: 100%; height: 150px; object-fit: cover; margin-bottom: 8px;">
                        <div class="infocontainer">
                            <div style="font-weight: bold; margin-bottom: 4px;color: red">${properties.points_of_interest}</div>
                            <div style="font-weight: bold;color:blue; margin-bottom: 4px;">${properties.nom_etablissement}</div>
                            <div style="font-weight: bold;">${properties.titre_affiche}</div>
                        </div>
                    </div>
                `
                : `
                    <div class="mappopup" style="width: 200px;padding:0; border:1px solid;margin:none;">
                        <img src="${properties.photos_projet?.split(',')[0].trim()}" alt="Project" style="width: 100%; height: 150px; object-fit: cover; margin-bottom: 8px;">
                        <div class="infocontainer">
                            <div style="font-weight: bold; margin-bottom: 4px;color: red">${properties.points_of_interest}</div>
                            <div style="margin-bottom: 4px;color:blue;font-weight: bold;">${properties.fiche_agence}</div>
                            <div style="font-weight: bold;">${properties.titre_affiche}</div>
                        </div>
                    </div>
                `;

            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            popup
                .setLngLat(coordinates)
                .setHTML(popupContent)
                .addTo(map.current);
        });

        map.current.on('mouseleave', 'vectorLayer', () => {
            map.current.getCanvas().style.cursor = '';
            popup.remove();
        });

    };

    // Combined data fetching function
    const initializeMapData = async (mapInstance) => {
        try {
            // Only show the selected item
            if (!selectedItem || !selectedItem.latitude || !selectedItem.longitude) {
                console.error('No valid selected item with coordinates');
                return;
            }

            // Create a single feature for the selected item
            const selectedFeature = {
                type: "Feature",
                properties: {
                    ...selectedItem,
                    projectType: selectedItem.type === 'event' ? 'itc' : selectedItem.type,
                    isSelected: true
                },
                geometry: {
                    type: "Point",
                    coordinates: [selectedItem.longitude, selectedItem.latitude]
                }
            };

            // Create GeoJSON with only the selected item
            const geoJsonData = {
                type: "FeatureCollection",
                features: [selectedFeature]
            };

            // Add or update source
            if (!mapInstance.getSource('vectorSource')) {
                mapInstance.addSource('vectorSource', {
                    type: 'geojson',
                    data: geoJsonData,
                    cluster: false
                });

                // Add vector layer with special styling for selected item
                mapInstance.addLayer({
                    id: 'vectorLayer',
                    type: 'symbol',
                    source: 'vectorSource',
                    filter: ['!', ['has', 'point_count']],
                    layout: {
                        'icon-image': [
                            'case',
                            ['==', ['get', 'isSelected'], true],
                            [
                                'match',
                                ['get', 'points_of_interest'],
                                'Retail', 'monthly-Retail',
                                'Hotel', 'monthly-Hotel',
                                'Office', 'monthly-Office',
                                'Restaurant/bar', 'monthly-Restaurant/bar',
                                'Cultural space', 'monthly-Cultural space',
                                'Private residence', 'monthly-Private residence',
                                'Urban space/Transportation', 'monthly-Urban space/Transportation',
                                'Architecture', 'monthly-Architecture',
                                'default-icon'
                            ],
                            ['==', ['get', 'projectType'], 'itc'],
                            'city-icon',
                            ['==', ['get', 'projectType'], 'pos'],
                            'points-of-sale-icon',
                            [
                                'match',
                                ['get', 'points_of_interest'],
                                'Retail', 'Retail',
                                'Hotel', 'Hotel',
                                'Office', 'Office',
                                'Restaurant/bar', 'Restaurant/bar',
                                'Cultural space', 'Cultural space',
                                'Private residence', 'Private residence',
                                'Urban space/Transportation', 'Urban space/Transportation',
                                'default-icon'
                            ]
                        ],
                        'icon-size': 0.6,
                        'icon-allow-overlap': true
                    }
                });

                sourceLoaded.current = true;
            } else {
                mapInstance.getSource('vectorSource').setData(geoJsonData);
            }

            // Zoom to selected item - matching project of the month behavior
            mapInstance.flyTo({
                center: [selectedItem.longitude, selectedItem.latitude],
                zoom: 15,
                duration: 2000
            });

            // Show popup automatically only for POS and design events, not for projects
            if (selectedItem.type === 'pos' || selectedItem.type === 'event') {
                setTimeout(() => {
                    const projectType = selectedItem.type === 'event' ? 'itc' : selectedItem.type;
                    
                    const popupContent = projectType === 'itc' 
                        ? `
                            <div class="mappopup" style="width: 200px; padding:0; border:1px solid; margin:none;">
                                <img src="${city_card}" alt="In The City" style="width: 80%; height: auto;display:block; margin-bottom: 8px;margin:auto">
                                <div class="infocontainer">
                                    <div style="margin-bottom: 4px; color:blue; font-weight: bold;">
                                        ${selectedItem.nom_etablissement}
                                    </div>
                                    <div style="font-size: 12px; text-transform:none; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;">
                                        ${selectedItem.description_projet || ''}
                                    </div>
                                </div>
                            </div>
                        `
                        : projectType === 'pos' 
                        ? `
                            <div class="mappopup" style="width: 200px;padding:0; border:1px solid;margin:none;">
                                <img style="max-width:80%; margin:auto;display:block" src="${selectedItem.photos_projet ? selectedItem.photos_projet.split(',')[0].trim() : retailIcon}" alt="Project">
                                <div class="infocontainer">
                                    <div style="font-weight: bold; margin-bottom: 4px;color: red">Point of sale</div>
                                    <div style="margin-bottom: 4px;color:blue;font-weight: bold;">${selectedItem.design_points_of_sales || ''}</div>
                                    <div style="font-weight: bold;">${selectedItem.titre_affiche || ''}</div>
                                </div>
                            </div>
                        `
                        : `
                            <div class="mappopup" style="width: 200px;padding:0; border:1px solid;margin:none;">
                                <div style="font-weight: bold;text-align:center">Projet</div>
                                ${selectedItem.photos_projet ? `<img src="${selectedItem.photos_projet.split(',')[0].trim()}" alt="Project" style="width: 100%; height: 150px; object-fit: cover; margin-bottom: 8px;">` : ''}
                                <div class="infocontainer">
                                    <div style="font-weight: bold; margin-bottom: 4px;color: red">${selectedItem.points_of_interest || ''}</div>
                                    <div style="font-weight: bold;color:blue; margin-bottom: 4px;">${selectedItem.nom_etablissement || ''}</div>
                                    <div style="font-weight: bold;">${selectedItem.titre_affiche || ''}</div>
                                </div>
                            </div>
                        `;

                    new mapboxgl.Popup({ offset: 35, closeButton: false, maxWidth: '300px' })
                        .setLngLat([selectedItem.longitude, selectedItem.latitude])
                        .setHTML(popupContent)
                        .addTo(mapInstance);
                }, 2500);
            }

        } catch (error) {
            console.error('Error initializing map data:', error);
        }
    };

    // Initial setup effect - wait for selectedItem
    useEffect(() => {
        if (!map.current && mapContainer.current && selectedItem) {
            console.log('Initializing map with selected item:', selectedItem);
            
            mapboxgl.accessToken = 'pk.eyJ1IjoiZGdjb2Jvc3MiLCJhIjoiY2xzY2JnajIzMGZsNzJpcGM4b3l5OWttaCJ9.1GzkF8EERgQ5u1jkmP3C7w';
            
            // Start with map centered on selected item
            const initialCenter = selectedItem.longitude && selectedItem.latitude
                ? [selectedItem.longitude, selectedItem.latitude]
                : [2.349014, 48.864716];
            const initialZoom = 10;
            
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: 'mapbox://styles/dgcoboss/cm5k9ztpe003g01s76e7v8owz',
                center: initialCenter,
                zoom: initialZoom,
                projection: 'mercator'
            });
    
            map.current.on('load', async () => {
                console.log('Map loaded, initializing data...');
                setMapLoaded(true);
                await loadAllIcons(map.current);
                await initializeMapData(map.current);
                setupPopupsAndHandlers();
            });
        }
    
        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [selectedItem]);
    
    // Update map when selected item changes
    useEffect(() => {
        if (map.current && sourceLoaded.current && selectedItem) {
            initializeMapData(map.current);
        }
    }, [selectedItem]);

    // Show loading state if no selected item yet
    if (!selectedItem) {
        return (
            <div style={{ 
                height: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#f0f0f0'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                    <div>Chargement de la carte...</div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <div className="design-map-container" style={{ position: 'relative', width: '100%', height: '100vh', borderTop:'solid 1px', borderBottom:'solid 1px' }}>
                <div 
                    ref={mapContainer} 
                    id="map" 
                    style={{ 
                        position: 'absolute', 
                        top: 0, 
                        bottom: 0, 
                        left: 0, 
                        right: 0 
                    }} 
                />
                {/* Debug info */}
                {!mapLoaded && (
                    <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'white',
                        padding: '10px',
                        borderRadius: '5px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        fontSize: '12px',
                        zIndex: 1000
                    }}>
                        <div>Item: {selectedItem?.titre_affiche || selectedItem?.nom_etablissement || 'Loading...'}</div>
                        <div>Lat: {selectedItem?.latitude}</div>
                        <div>Lng: {selectedItem?.longitude}</div>
                        <div>Type: {selectedItem?.type}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorldmapAdminView;